import { axiosClient } from '@nx-frontend/shared/api';

export class TasksApiProxyService {
  private axiosParams: { headers?: { [key: string]: string } } = {};

  constructor(private gatewayUrl: string, private sessionId: string) {
    this.axiosParams = {
      headers: {
        'everhelper-session-id': this.sessionId,
      },
    };
  }

  async apiCreateTaskList(workspaceId: string, payload: { title: string; globalId: string; portalId?: string | null }) {
    const requestUrl = `svc:note-task/workspaces/${workspaceId}/taskLists`;
    const { data } = await axiosClient.post(`${this.gatewayUrl}/${requestUrl}`, payload, this.axiosParams);
    return data;
  }

  async apiCreateTaskBoard(payload: {
    title: string;
    globalId: string;
    noteId: string;
    workspaceId: string;
    columnNames?: string[];
  }) {
    const requestUrl = `ft:tasks/boards`;
    const { data } = await axiosClient.post(
      `${this.gatewayUrl}/${requestUrl}`,
      {
        ...payload,
      },
      this.axiosParams
    );
    return data;
  }

  async apiDeleteTaskList(workspaceId: string, taskListId: string, readOnly: boolean) {
    const requestUrl = `ft:tasks/workspaces/${workspaceId}/taskLists/${taskListId}`;
    const { data } = await axiosClient.delete(
      `${this.gatewayUrl}/${requestUrl}${readOnly ? '' : '?reason=editor'}`,
      this.axiosParams
    );
    return data;
  }

  async apiRestoreTaskList(workspaceId: string, noteGlobalId: string, taskListId: string) {
    const requestUrl = `ft:tasks/workspaces/${workspaceId}/taskLists/${taskListId}/restore`;
    const { data } = await axiosClient.post(
      `${this.gatewayUrl}/${requestUrl}`,
      {
        payload: {
          noteGlobalId,
        },
      },
      this.axiosParams
    );
    return data;
  }

  async apiDeleteTaskBoard(boardId: string) {
    const requestUrl = `ft:tasks/boards/${boardId}`;
    const { data } = await axiosClient.delete(`${this.gatewayUrl}/${requestUrl}?reason=editor`, this.axiosParams);
    return data;
  }

  async apiRestoreTaskBoard(boardId: string) {
    const requestUrl = `ft:tasks/boards/${boardId}/restore`;
    const { data } = await axiosClient.post(`${this.gatewayUrl}/${requestUrl}`, {}, this.axiosParams);
    return data;
  }

  async apiDuplicateTaskBoard(payload: { from: string; to: string; toNote?: string; toWorkspace?: string }) {
    const requestUrl = `ft:tasks/boards/copy`;
    const { data } = await axiosClient.post(
      `${this.gatewayUrl}/${requestUrl}`,
      {
        ...payload,
      },
      this.axiosParams
    );
    return data;
  }

  async apiCreateTaskBoardColumn(payload: {
    globalId?: string;
    title?: string;
    boardId: string;
    before?: string;
    from?: string;
  }) {
    const requestUrl = `ft:tasks/board-columns`;
    const { data } = await axiosClient.post(
      `${this.gatewayUrl}/${requestUrl}`,
      {
        ...payload,
      },
      this.axiosParams
    );
    return data;
  }

  async apiDuplicateTaskList(
    workspaceId: string,
    taskListId: string,
    noteTaskCopyTaskListData: { noteGlobalId: string; workspaceId: string; updateBlot: boolean }
  ) {
    const requestUrl = `svc:note-task/workspaces/${workspaceId}/taskLists`;
    const { data } = await axiosClient.post(
      `${this.gatewayUrl}/${requestUrl}/${taskListId}/copy`,
      noteTaskCopyTaskListData,
      this.axiosParams
    );
    return data;
  }

  async apiCreateTasks(workspaceId: string, tasks: { taskListId: string; title: string; globalId: string }[]) {
    const requestUrl = `ft:tasks/workspaces/${workspaceId}/tasks`;

    return tasks.map((task) => {
      return axiosClient.post(`${this.gatewayUrl}/${requestUrl}`, { task }, this.axiosParams);
    });
  }

  async apiExtractColumn(workspaceId: string, noteId: string, taskListId: string) {
    const requestUrl = `ft:tasks/board-columns/extract`;
    return axiosClient.post(`${this.gatewayUrl}/${requestUrl}`, { workspaceId, noteId, taskListId }, this.axiosParams);
  }

  async apiUpdateTaskList(workspaceId: string, taskListId: string, data: { order: string[] }) {
    const requestUrl = `svc:note-task/workspaces/${workspaceId}/taskLists/${taskListId}`;
    return axiosClient.post(`${this.gatewayUrl}/${requestUrl}`, data, this.axiosParams);
  }

  /**
   * Create task list with tasks
   * @param workspaceId
   * @param noteId
   * @param list
   * @returns
   */
  async createTasks(workspaceId: string, noteId: string, list: string[]): Promise<{ data: { taskListId: string } }> {
    const requestUrl = `ft:tasks/createTasks`;
    return axiosClient.post(
      `${this.gatewayUrl}/${requestUrl}`,
      {
        workspaceId,
        noteId,
        list,
      },
      this.axiosParams
    );
  }
}
